import { ref } from 'vue';

export function useUserLocation() {
    const userLocation = ref(null);
    const userLocationError = ref(null);
    const userLocationloading = ref(false);
    const permissionStatus = ref('prompt'); // 'granted', 'denied', 'prompt'
    
    // Check for cached location in localStorage
    const getCachedLocation = () => {
        try {
            const cached = localStorage.getItem('user_location');
            if (cached) {
                const parsed = JSON.parse(cached);
                const now = Date.now();
                // Cache valid for 1 hour (3600000 ms)
                if (parsed.timestamp && (now - parsed.timestamp) < 3600000) {
                    userLocation.value = {
                        lat: parsed.lat,
                        lng: parsed.lng
                    };

                    return true;
                }
            }
        } catch (error) {
            console.warn('Error reading cached location:', error);
        }
        return false;
    };
    
    // Cache location to localStorage
    const cacheLocation = (location) => {
        try {
            const cacheData = {
                lat: location.lat,
                lng: location.lng,
                timestamp: Date.now()
            };
            localStorage.setItem('user_location', JSON.stringify(cacheData));
        } catch (error) {
            console.warn('Error caching location:', error);
        }
    };

    // Check browser permission status
    const checkPermissionStatus = async () => {
        if ('permissions' in navigator) {
            try {
                const permission = await navigator.permissions.query({ name: 'geolocation' });
                permissionStatus.value = permission.state;

                
                // Listen for permission changes
                permission.addEventListener('change', () => {
                    permissionStatus.value = permission.state;

                    
                    // If permission was denied, clear cached location
                    if (permission.state === 'denied') {
                        userLocation.value = null;
                        localStorage.removeItem('user_location');
                    }
                });
                
                return permission.state;
            } catch (error) {
                console.warn('Error checking geolocation permission:', error);
                return 'prompt';
            }
        }
        return 'prompt';
    };

    // Get current position
    const getCurrentPosition = () => {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const location = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    resolve(location);
                },
                (error) => {
                    reject(error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000, // 10 seconds
                    maximumAge: 300000 // 5 minutes
                }
            );
        });
    };
    
    const getLocation = async () => {
        // First check if we already have a valid cached location
        if (getCachedLocation()) {
            return;
        }
        

        userLocationloading.value = true;
        userLocationError.value = null;
        
        if (!navigator.geolocation) {

            userLocationError.value = 'Geolocation is not supported by your browser';
            userLocationloading.value = false;
            return;
        }

        try {
            // Check permission status first
            const permission = await checkPermissionStatus();
            
            if (permission === 'denied') {
                userLocationError.value = 'Location access denied. Please enable location access in your browser settings to see nearby dealers.';
                userLocationloading.value = false;
                return;
            }

            // Try to get current position
            const location = await getCurrentPosition();
            

            userLocation.value = location;
            userLocationloading.value = false;
            
            // Cache the location
            cacheLocation(location);
            

            
        } catch (err) {

            
            // Handle specific error types
            let errorMessage = 'Failed to retrieve location';
            if (err.code === 1) { // PERMISSION_DENIED
                errorMessage = 'Location access denied. Please enable location access to see nearby dealers.';
            } else if (err.code === 2) { // POSITION_UNAVAILABLE
                errorMessage = 'Location information is unavailable.';
            } else if (err.code === 3) { // TIMEOUT
                errorMessage = 'Location request timed out. Please try again.';
            }
            
            userLocationError.value = errorMessage;
            userLocationloading.value = false;
        }
    };

    // Initialize: Check permission and use cached location if available and permission is granted
    const initializeLocation = async () => {
        const permission = await checkPermissionStatus();
        
        // If permission is granted and we have cached location, use it
        if (permission === 'granted' && getCachedLocation()) {

            return;
        }
        
        // If permission is granted but no cached location, get fresh location
        if (permission === 'granted') {

            await getLocation();
        }
        
        // If permission is prompt, we'll wait for user action
        // If permission is denied, we won't automatically request
    };

    return {
        userLocation,
        userLocationError,
        userLocationloading,
        permissionStatus,
        getLocation,
        initializeLocation
    }
}